import { useOrgUsers } from "@/api/users";
import { Slider } from "@/components/ui/slider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { PersonAvatar, Spinner } from "@cohesive/ui-components";
import {
  useHistoryVersions,
  useHistoryVersionStorageData,
} from "@liveblocks/react";
import type { Edge, Node } from "@xyflow/react";
import { format } from "date-fns";
import { Crosshair, History, Pause, Play, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CanvasToolbarButton,
  CanvasToolbarDisplay,
  CanvasToolbarDivider,
  CanvasToolbarGroup,
  CanvasToolbarShell,
} from "../components/toolbar";
import {
  useCanvasReplay,
  type CanvasReplayFrame,
} from "../context/CanvasReplayContext";
import { REPLAY_COPY } from "./replayCopy";
import { useReplayFollow } from "./useReplayFollow";

const SPEEDS = [1, 2, 4] as const;
const FRAME_MS = 1_000;
// Long enough for the camera to arrive and the change to land.
const FOLLOW_FRAME_MS = 1_600;
const CAMERA_MS = 600;
const PREFETCH_BEHIND = 2;
const PREFETCH_AHEAD = 3;

type FlowJson = {
  flow?: {
    nodes?: Record<string, Node>;
    edges?: Record<string, Edge>;
  };
};

/** Fetches one version's storage and hands it up as a frame; renders nothing. */
function VersionFrameLoader({
  versionId,
  onLoad,
}: {
  versionId: string;
  onLoad: (versionId: string, frame: CanvasReplayFrame | Error) => void;
}) {
  const { data, error } = useHistoryVersionStorageData(versionId);
  useEffect(() => {
    if (error) onLoad(versionId, error);
    if (!data) return;
    const { flow } = data.toJSON() as FlowJson;
    onLoad(versionId, {
      nodes: Object.values(flow?.nodes ?? {}),
      edges: Object.values(flow?.edges ?? {}),
    });
  }, [data, error, versionId, onLoad]);
  return null;
}

/** Toolbar entry point, beside Share. */
export function CanvasReplayButton() {
  const replay = useCanvasReplay();
  if (!replay) return null;
  return (
    <TooltipProvider>
      <CanvasToolbarShell aria-label={REPLAY_COPY.replay}>
        <CanvasToolbarButton
          label={REPLAY_COPY.replay}
          tooltipSide="bottom"
          active={replay.replaying}
          onClick={replay.replaying ? replay.stop : replay.start}
        >
          <History className="size-4" strokeWidth={2} />
        </CanvasToolbarButton>
      </CanvasToolbarShell>
    </TooltipProvider>
  );
}

export function CanvasReplayBar() {
  const replay = useCanvasReplay();
  const { versions, isLoading, error } = useHistoryVersions();
  // Oldest left. Sorted, not reversed: the SDK lists the first fetch newest
  // first but appends versions found by its later polls at the end.
  const ordered = useMemo(
    () =>
      [...(versions ?? [])].sort(
        (a, b) => a.createdAt.getTime() - b.createdAt.getTime(),
      ),
    [versions],
  );

  const [index, setIndex] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<(typeof SPEEDS)[number]>(1);
  const [scrubbing, setScrubbing] = useState(false);
  const [frames, setFrames] = useState<
    ReadonlyMap<string, CanvasReplayFrame | Error>
  >(() => new Map());

  // Open on the newest version.
  useEffect(() => {
    if (index === null && ordered.length > 0) setIndex(ordered.length - 1);
  }, [index, ordered.length]);

  const current = index === null ? undefined : ordered[index];
  const currentFrame = current ? frames.get(current.id) : undefined;
  const lastIndex = ordered.length - 1;

  const onLoad = useCallback(
    (versionId: string, frame: CanvasReplayFrame | Error) =>
      setFrames((prev) => new Map(prev).set(versionId, frame)),
    [],
  );

  const prefetchIds = useMemo(() => {
    if (index === null) return [];
    const from = Math.max(0, index - PREFETCH_BEHIND);
    const to = Math.min(lastIndex, index + PREFETCH_AHEAD);
    return ordered
      .slice(from, to + 1)
      .map((v) => v.id)
      .filter((id) => !frames.has(id));
  }, [index, lastIndex, ordered, frames]);

  const shownFrame = currentFrame instanceof Error ? undefined : currentFrame;
  const setFrame = replay?.setFrame;
  useEffect(() => {
    if (shownFrame) setFrame?.(shownFrame);
  }, [shownFrame, setFrame]);

  const follow = replay?.follow ?? false;
  const setFollow = replay?.setFollow;
  useReplayFollow({
    frame: shownFrame,
    follow,
    holding: scrubbing,
    durationMs: CAMERA_MS / speed,
  });

  const currentFailed = currentFrame instanceof Error;
  const nextFrame =
    index === null || !ordered[index + 1]
      ? undefined
      : frames.get(ordered[index + 1].id);
  const nextReady = !!nextFrame && !(nextFrame instanceof Error);
  const nextFailed = nextFrame instanceof Error;

  // Advance only once the next frame is in hand, so playback never shows a gap.
  useEffect(() => {
    if (!playing || index === null) return;
    if (index >= lastIndex || currentFailed) {
      setPlaying(false);
      return;
    }
    if (nextFailed) {
      setPlaying(false);
      setIndex(index + 1);
      return;
    }
    if (!nextReady || !shownFrame) return;
    const timer = window.setTimeout(
      () => setIndex(index + 1),
      (follow ? FOLLOW_FRAME_MS : FRAME_MS) / speed,
    );
    return () => window.clearTimeout(timer);
  }, [
    playing,
    index,
    lastIndex,
    currentFailed,
    nextFailed,
    nextReady,
    shownFrame,
    speed,
    follow,
  ]);

  // Dropping failed frames remounts their loaders, which retries them.
  const dropErrors = useCallback(
    () =>
      setFrames((prev) =>
        [...prev.values()].some((f) => f instanceof Error)
          ? new Map([...prev].filter(([, f]) => !(f instanceof Error)))
          : prev,
      ),
    [],
  );

  const step = useCallback(
    (delta: number) => {
      setPlaying(false);
      dropErrors();
      setIndex((i) =>
        i === null ? i : Math.min(lastIndex, Math.max(0, i + delta)),
      );
    },
    [lastIndex, dropErrors],
  );

  const togglePlay = useCallback(() => {
    if (index === null) return;
    if (!playing) dropErrors();
    if (!playing && index >= lastIndex) setIndex(0);
    setPlaying((p) => !p);
  }, [index, lastIndex, playing, dropErrors]);

  const exit = replay?.stop;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target?.closest("input, textarea, [contenteditable='true']") ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey
      ) {
        return;
      }
      const handlers: Record<string, () => void> = {
        ArrowLeft: () => step(-1),
        ArrowRight: () => step(1),
        " ": togglePlay,
        Escape: () => exit?.(),
      };
      const handler = handlers[event.key];
      if (!handler) return;
      // Capture phase, so the canvas's own Space (pan) and Escape never see it.
      event.preventDefault();
      event.stopPropagation();
      handler();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [step, togglePlay, exit]);

  const { data: users } = useOrgUsers();
  const authors = useMemo(() => {
    const byId = new Map((users ?? []).map((u) => [u.id, u]));
    return (current?.authors ?? []).map((a) => ({
      id: a.id,
      user: byId.get(a.id),
    }));
  }, [current, users]);

  const status = error
    ? REPLAY_COPY.loadFailed
    : !isLoading && ordered.length === 0
      ? REPLAY_COPY.empty
      : null;
  const loadingFrame = !!current && currentFrame === undefined;

  return (
    <TooltipProvider>
      {prefetchIds.map((id) => (
        <VersionFrameLoader key={id} versionId={id} onLoad={onLoad} />
      ))}
      <div
        className="absolute bottom-6 left-1/2 z-50 -translate-x-1/2"
        data-canvas-control
      >
        <CanvasToolbarShell
          register="primary"
          aria-label={REPLAY_COPY.replay}
          className="px-1.5 py-1 gap-2"
        >
          {status ? (
            <CanvasToolbarDisplay className="px-3 text-sm text-n-60">
              {status}
            </CanvasToolbarDisplay>
          ) : index === null ? (
            <span className="px-3">
              <Spinner size="sm" />
            </span>
          ) : (
            <>
              <CanvasToolbarGroup>
                <CanvasToolbarButton
                  label={playing ? REPLAY_COPY.pause : REPLAY_COPY.play}
                  onClick={togglePlay}
                >
                  {playing ? (
                    <Pause className="size-4" strokeWidth={2} />
                  ) : (
                    <Play className="size-4" strokeWidth={2} />
                  )}
                </CanvasToolbarButton>
                <CanvasToolbarButton
                  label={REPLAY_COPY.speed}
                  monoLabel={`${speed}×`}
                  onClick={() =>
                    setSpeed(
                      SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length],
                    )
                  }
                />
                <CanvasToolbarButton
                  label={REPLAY_COPY.follow}
                  active={follow}
                  aria-pressed={follow}
                  onClick={() => setFollow?.(!follow)}
                >
                  <Crosshair className="size-4" strokeWidth={2} />
                </CanvasToolbarButton>
              </CanvasToolbarGroup>

              <div className="flex w-[min(420px,40vw)] flex-col gap-1 px-1">
                <Slider
                  min={0}
                  max={Math.max(lastIndex, 1)}
                  step={1}
                  value={[index]}
                  disabled={lastIndex < 1}
                  onValueChange={([v]) => {
                    setPlaying(false);
                    setScrubbing(true);
                    setIndex(v);
                  }}
                  onValueCommit={() => setScrubbing(false)}
                  aria-label={REPLAY_COPY.version}
                />
                <div className="flex justify-between px-[9px]" aria-hidden>
                  {ordered.map((v, i) => (
                    <span
                      key={v.id}
                      className={
                        i <= index ? "h-1.5 w-px bg-n-60" : "h-1.5 w-px bg-n-30"
                      }
                    />
                  ))}
                </div>
              </div>

              <div className="flex min-w-[148px] items-center gap-2">
                <div className="flex flex-col leading-tight">
                  <span className="font-mono text-xs text-n-90">
                    {current
                      ? format(current.createdAt, "MMM d, HH:mm:ss")
                      : ""}
                  </span>
                  <span className="font-mono text-[11px] text-n-50">
                    {index + 1} / {ordered.length}
                  </span>
                </div>
                {loadingFrame ? (
                  <Spinner size="sm" />
                ) : currentFailed ? (
                  <span className="text-xs text-n-60">
                    {REPLAY_COPY.loadFailed}
                  </span>
                ) : (
                  <div className="flex -space-x-1.5">
                    {authors.slice(0, 3).map(({ id, user }) => (
                      <PersonAvatar
                        key={id}
                        name={user?.name}
                        email={user?.email}
                        pictureUrl={user?.picture_url}
                        className="size-6 text-[10px] ring-2 ring-n-05 dark:ring-n-30"
                      />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
          <CanvasToolbarDivider />
          <CanvasToolbarButton label={REPLAY_COPY.exit} onClick={exit}>
            <X className="size-4" strokeWidth={2} />
          </CanvasToolbarButton>
        </CanvasToolbarShell>
      </div>
    </TooltipProvider>
  );
}
